import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { api, fmtErr } from "../../lib/api";
import { PageHeader } from "./Dashboard";
import {
  Plus, Trash2, Package, AlertCircle, Pencil, History, Info, ScanLine,
  MinusCircle, PlusCircle, Wrench, X, Camera, ChevronRight, Mail, Copy, ClipboardList,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import InventoryConsumption from "./InventoryConsumption";
import { useRights } from "../../lib/useRights";
import { AuthImage } from "../../components/FileUploader";
import { useTour } from "../../components/tour/TourContext";
import TourReplayButton from "../../components/tour/TourReplayButton";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import {
  MAIN_CATEGORIES, TYPES_BY_CATEGORY, mainCategoryForItem,
} from "../../lib/inventoryCategories";
import ManageTypesDialog from "../../components/ManageTypesDialog";

const DEFAULT = {
  name: "", group: MAIN_CATEGORIES[0], category: "", brand: "", part_number: "",
  quantity: 0, min_stock: 0, location: "", supplier: "", supplier_id: "",
  price: "", notes: "", expiry_date: "", photo_data_url: "", photo_path: "",
};

// Resize an uploaded image to a target width (height auto, aspect kept) and
// re-encode as JPEG quality 0.8. We use a hidden <canvas> in the browser so
// the bytes hitting the API are tiny — typically 8–35 KB at 300px wide,
// regardless of whether the user drops in a 12-megapixel iPhone photo.
export async function resizeImageToDataUrl(fileOrUrl, targetWidth = 300) {
  // Accept either a File/Blob (PhotoPicker path) or a URL string (barcode
  // lookup path — OpenFoodFacts product image). For URLs we go through
  // fetch → blob so the same canvas pipeline can read the pixels even
  // when the source is cross-origin (the response carries CORS headers).

  let blob;
  if (typeof fileOrUrl === "string") {
    const r = await fetch(fileOrUrl);
    if (!r.ok) throw new Error(`image fetch failed: ${r.status}`);
    blob = await r.blob();
  } else {
    blob = fileOrUrl;
  }
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  // Skip the resize when the source is already smaller than the target —
  // up-scaling would just blur the thumb without saving any bytes.
  const ratio = img.width > targetWidth ? targetWidth / img.width : 1;
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  // Use JPEG to keep size predictable; PNG would balloon for photos. The
  // 0.82 quality is a sweet spot — visually indistinguishable at 300 px wide.
  return canvas.toDataURL("image/jpeg", 0.82);
}


// Days until an item's expiry date (null when none set). Negative = expired.
function expiryDays(it) {
  if (!it.expiry_date) return null;
  const exp = new Date(it.expiry_date + "T00:00:00");
  if (isNaN(exp)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((exp - today) / 86400000);
}

// Amber "expires soon" (≤7 days) / red "expired" pill, or null.
function ExpiryBadge({ it }) {
  const d = expiryDays(it);
  if (d === null || d > 7) return null;
  const expired = d < 0;
  const label = expired ? "Expired" : d === 0 ? "Expires today" : `Expires in ${d}d`;
  return (
    <span
      data-testid={`inv-expiry-${it.id}`}
      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none ${expired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}
    >
      {label}
    </span>
  );
}


// Resolve an item's photo to a displayable URL. Photos live in R2 behind the
// authenticated /files endpoint (photo_path); legacy pre-migration rows may
// still carry an inline base64 data URL, used as-is until the startup
// migration clears them.
function useItemPhotoUrl(item) {
  const path = item?.photo_path;
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) { setUrl(null); return; }
    let alive = true, blobUrl;
    (async () => {
      try {
        const r = await api.get(`/files/${path}`, { responseType: "blob" });
        if (!alive) return;
        blobUrl = URL.createObjectURL(r.data);
        setUrl(blobUrl);
      } catch { /* keep placeholder */ }
    })();
    return () => { alive = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]);
  return (path && url) || item?.photo_data_url || "";
}


// Small "Use 1" pill — logs a one-unit consumption without opening a dialog.
function Use1Button({ it, onQuickUse, tid = "inv-use1" }) {
  if (!onQuickUse || (it.quantity ?? 0) <= 0) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onQuickUse(it); }}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-ink bg-white border border-zinc-300 rounded-md px-2.5 py-1.5 sm:py-0.5 hover:bg-zinc-50 active:bg-zinc-100 shrink-0 transition-colors"
      data-testid={`${tid}-${it.id}`}
      title="Log 1 unit used"
    >
      Use 1
    </button>
  );
}


// Presentational item renderers, shared by every collapsible group section.
// Pure — they take the item + an `onOpen(it)` callback (Adjust or History,
// decided by the parent based on the user's rights).
function InvCard({ it, onOpen, onQuickUse }) {
  const low = (it.quantity ?? 0) <= (it.min_stock ?? 0);
  const photo = useItemPhotoUrl(it);
  return (
    <li
      className="rounded-md border border-zinc-200 bg-white p-3 flex flex-col gap-2 cursor-pointer hover:border-zinc-300 active:bg-zinc-50 transition-colors overflow-hidden"
      data-testid={`inv-card-${it.id}`}
      onClick={() => onOpen(it)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(it); } }}
    >
      <div className="flex items-start justify-between gap-3 min-w-0">
        {photo && (
          <img src={photo} alt="" className="rounded-sm border border-zinc-200 object-cover shrink-0" style={{ width: 60, height: 60 }} />
        )}
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
            {low && <AlertCircle className="h-3.5 w-3.5 text-amber-600 shrink-0" />}
            <span className="font-medium text-sm truncate">{it.name}</span>
            <ExpiryBadge it={it} />
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
            {it.category}
            {it.brand && <> <span className="text-zinc-300">·</span> {it.brand}</>}
          </div>
          {it.part_number && (
            <div className="text-[11px] text-zinc-500 font-mono mt-0.5 break-all">{it.part_number}</div>
          )}
        </div>
        <div className="text-right shrink-0 flex flex-col items-end gap-1">
          <div className={`font-mono text-base leading-tight ${low ? "text-amber-700" : "text-zinc-800"}`}>{it.quantity}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">min {it.min_stock ?? 0}</div>
          <Use1Button it={it} onQuickUse={onQuickUse} tid="inv-use1-card" />
        </div>
      </div>
      {it.location && <div className="text-[11px] text-zinc-500 truncate">{it.location}</div>}
    </li>
  );
}

function InvRow({ it, onOpen, onQuickUse }) {
  const low = (it.quantity ?? 0) <= (it.min_stock ?? 0);
  const photo = useItemPhotoUrl(it);
  return (
    <tr data-testid={`inv-row-${it.id}`} onClick={() => onOpen(it)} className="cursor-pointer hover:bg-zinc-50 transition-colors align-top">
      <Td className="break-words">
        <div className="flex items-start gap-2 flex-wrap">
          {low && <AlertCircle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />}
          <span className="font-medium break-words">{it.name}</span>
          <ExpiryBadge it={it} />
        </div>
        {it.brand && <div className="text-xs text-zinc-500 break-words">{it.brand}</div>}
      </Td>
      <Td className="text-zinc-600 break-words">{it.category}</Td>
      <Td>
        {photo ? (
          <img src={photo} alt="" className="rounded-sm border border-zinc-200 object-cover" style={{ width: 60, height: 60 }} data-testid={`thumb-${it.id}`} />
        ) : (
          <span className="text-zinc-300 text-xs">—</span>
        )}
      </Td>
      <Td className={`text-right font-mono ${low ? "text-amber-700" : ""}`}>{it.quantity}</Td>
      <Td className="text-right font-mono text-zinc-500">{it.min_stock}</Td>
      <Td className="text-zinc-600 break-words">{it.location || "—"}</Td>
      <Td className="text-right"><Use1Button it={it} onQuickUse={onQuickUse} /></Td>
    </tr>
  );
}


// ───────────────────────────────────────────────────────────────────────
// OpenFoodFacts barcode lookup
// ───────────────────────────────────────────────────────────────────────
// OFF is the most comprehensive FREE (no API key, CORS-enabled) barcode
// database for consumables. Coverage is excellent for cleaning products,
// galley supplies and crew/guest supplies — the bulk of yacht inventory.
// Spare parts and tools are usually NOT found there; in that case we
// fall through and let the user fill the rest manually with the barcode
// pre-populated in `part_number`.
//
// Returns ``{name, brand, image_url, category}`` (any field may be empty).
async function lookupBarcode(code) {
  try {
    const r = await fetch(`https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(code)}.json`);
    if (!r.ok) return {};
    const d = await r.json();
    if (d.status !== 1 || !d.product) return {};
    const p = d.product;
    return {
      name: (p.product_name_en || p.product_name || "").trim(),
      brand: (p.brands || "").split(",")[0].trim(),
      image_url: p.image_front_url || p.image_url || "",
      category: mapOffCategoryToOurs(p.categories_tags || []),
    };
  } catch {
    return {};
  }
}

// OpenFoodFacts uses english tags like "en:cleaners", "en:detergents",
// "en:beverages", "en:foods". Map a few common families onto our preset
// list so the dropdown lands on a sensible default. Anything we don't
// recognise leaves the user on the current value (which is the dialog's
// existing default — usually "Spare parts").
function mapOffCategoryToOurs(tags) {
  const t = tags.map((x) => String(x).toLowerCase());
  if (t.some((x) => x.includes("cleaner") || x.includes("detergent") || x.includes("soap"))) {
    return "Cleaning products";
  }
  // F&B branches — check most-specific buckets first so generic "beverage"
  // doesn't swallow champagne / spirits etc.
  if (t.some((x) => x.includes("beer") || x.includes("ale") || x.includes("lager"))) {
    return "Beer";
  }
  if (t.some((x) => x.includes("fruit") || x.includes("apple") || x.includes("banana")
                  || x.includes("orange") || x.includes("berry") || x.includes("citrus"))) {
    return "Fruits";
  }
  if (t.some((x) => x.includes("water") && !x.includes("watermelon"))) return "Water";
  if (t.some((x) => x.includes("spirit") || x.includes("whisky") || x.includes("whiskey")
                  || x.includes("vodka") || x.includes("gin") || x.includes("rum")
                  || x.includes("tequila") || x.includes("liquor") || x.includes("liqueur"))) {
    return "Spirits";
  }
  if (t.some((x) => x.includes("wine") || x.includes("champagne") || x.includes("sparkling"))) {
    return "Wine/Champagne";
  }
  if (t.some((x) => x.includes("soft-drink") || x.includes("soft_drink") || x.includes("soda")
                  || x.includes("cola") || x.includes("juice") || x.includes("beverage"))) {
    return "Soft drinks";
  }
  if (t.some((x) => x.includes("snack") || x.includes("chips") || x.includes("biscuit")
                  || x.includes("cookie") || x.includes("crisp"))) {
    return "Snacks";
  }
  if (t.some((x) => x.includes("medic") || x.includes("pharma"))) return "Medication";
  if (t.some((x) => x.includes("cosmetic") || x.includes("beauty") || x.includes("hygien"))) {
    return "Toiletries";
  }
  return "";
}

const REASON_LABELS = {
  used: "Used",
  broken: "Broken / lost",
  restocked: "Restocked",
  adjusted: "Manual adjustment",
};

export default function Inventory() {
  const { t } = useTranslation();
  const { activeYacht } = useYacht();
  const { can } = useRights();
  const { activeTour } = useTour();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT);
  const [editingId, setEditingId] = useState(null);
  const [manageValuesKind, setManageValuesKind] = useState(null); // "types" | "locations" | null
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [typ, setTyp] = useState("");
  const [loc, setLoc] = useState("");
  // Groups render collapsed by default; expandedGroups tracks the ones the
  // user opened. Active search/filters force everything open so matches show.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (g) => setExpandedGroups((s) => {
    const n = new Set(s);
    n.has(g) ? n.delete(g) : n.add(g);
    return n;
  });
  const [adjustItem, setAdjustItem] = useState(null);  // item to adjust
  // AI photo matched an item already in stock → ask instead of duplicating.
  const [dupPrompt, setDupPrompt] = useState(null);     // {existing, prefill}
  // Multi-product AI scan review rows: [{...detected, include, qty, existing}]
  const [multiScan, setMultiScan] = useState(null);
  const [multiBusy, setMultiBusy] = useState(false);
  const [historyItem, setHistoryItem] = useState(null); // item to view history of
  const [detailItem, setDetailItem] = useState(null);   // item to view full details
  const [reorderGroup, setReorderGroup] = useState(null); // main category to build a reorder list for
  const [scannerOpen, setScannerOpen] = useState(false);
  const [view, setView] = useState("items"); // 'items' | 'consumption'
  // ?filter=low-stock pre-applies a low-stock-only filter (dashboard
  // "Low stock items" card). Cleared by user clicking the chip.
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("filter") === "low-stock") {
      setLowStockOnly(true);
    }
    if (searchParams.get("filter") === "expiring") {
      setExpiringOnly(true);
    }
    const qp = searchParams.get("q");
    if (qp) setQ(qp);
    if (searchParams.get("filter") || qp) {
      const next = new URLSearchParams(searchParams);
      next.delete("filter");
      next.delete("q");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/inventory?yacht_id=${activeYacht.id}`);
      setItems(data);
    } catch {
      setItems([]);
    }
  };

  // Suppliers feeding the "Supplier" dropdown on the create/edit form.
  // Loaded once per yacht; users with no supplier:read access will silently
  // get an empty list (the dropdown then only offers "Other (typed)").
  const loadSuppliers = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/suppliers?yacht_id=${activeYacht.id}`);
      setSuppliers(Array.isArray(data) ? data : []);
    } catch {
      setSuppliers([]);
    }
  };

  useEffect(() => {
    setItems([]);
    setSuppliers([]);
    load();
    loadSuppliers();
    /* eslint-disable-next-line */
  }, [activeYacht?.id]);

  const openCreate = () => { setEditingId(null); setForm(DEFAULT); setOpen(true); };

  // "AI Photo" flow: user snaps/uploads a photo of an item → vision model
  // identifies it and prefills the form; the user only enters quantity and
  // storage location. The photo itself becomes the item thumbnail.
  const aiPhotoInputRef = useRef(null);
  const handleAiPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setEditingId(null);
    let detectUrl = "";
    let thumbUrl = "";
    try {
      detectUrl = await resizeImageToDataUrl(file, 1400);
      thumbUrl = await resizeImageToDataUrl(file, 300);
    } catch {
      toast.error("Could not read that photo — try another one");
      return;
    }
    setForm({ ...DEFAULT, photo_data_url: thumbUrl });
    setOpen(true);
    // Persistent loading toast — stays up until the AI answers so the user
    // always knows a scan is in progress (updated in place by id below).
    toast.loading("Analyzing photo with AI… this takes a few seconds", { id: "ai-scan", duration: Infinity });
    try {
      const { data } = await api.post("/inventory/ai-detect", { image_base64: detectUrl });
      const detectedList = (Array.isArray(data.items) ? data.items : [data]).filter((d) => d && d.name);
      const norm = (v) => String(v || "").trim().toLowerCase();
      const findExisting = (d) => items.find((it) =>
        (d.part_number && norm(it.part_number) === norm(d.part_number)) ||
        (d.name && norm(it.name) === norm(d.name)));
      // Several DIFFERENT products in one photo → batch review dialog.
      if (detectedList.length > 1) {
        setOpen(false);
        setMultiScan(detectedList.slice(0, 8).map((d) => ({
          ...d, include: true, qty: d.quantity || 1, existing: findExisting(d) || null,
        })));
        toast.success(`Detected ${detectedList.length} different products — review & add`, { id: "ai-scan", duration: 6000 });
        return;
      }
      const d0 = detectedList[0] || data;
      // Duplicate check: same part number or same name already in stock.
      const existing = d0.name ? findExisting(d0) : null;
      if (existing) {
        setOpen(false);
        toast.success(`Detected: ${d0.name} — already in your inventory`, { id: "ai-scan", duration: 6000 });
        setDupPrompt({
          existing,
          detectedQty: d0.quantity || null,
          prefill: {
            ...DEFAULT, photo_data_url: thumbUrl,
            name: d0.name || "", brand: d0.brand || "",
            part_number: d0.part_number || "",
            quantity: d0.quantity || 0,
            expiry_date: d0.expiry_date || "",
            group: d0.group || DEFAULT.group, category: d0.category || DEFAULT.category,
          },
        });
        return;
      }
      setForm((prev) => ({
        ...prev,
        name: prev.name || d0.name || "",
        brand: prev.brand || d0.brand || "",
        part_number: prev.part_number || d0.part_number || "",
        group: d0.group || prev.group,
        category: d0.category || prev.category,
        quantity: prev.quantity || d0.quantity || prev.quantity,
        expiry_date: prev.expiry_date || d0.expiry_date || "",
      }));
      if (d0.name) {
        const extras = [
          d0.quantity > 1 ? `counted ${d0.quantity} pieces` : null,
          d0.expiry_date ? `expiry ${d0.expiry_date}` : null,
        ].filter(Boolean).join(", ");
        toast.success(
          `Detected: ${d0.name}${d0.brand ? ` · ${d0.brand}` : ""}${extras ? ` — ${extras}` : " — just add quantity & location"}`,
          { id: "ai-scan", duration: 8000 },
        );
      } else {
        toast.warning("Couldn't identify the item — fill the details manually", { id: "ai-scan", duration: 8000 });
      }
    } catch (err) {
      toast.error(fmtErr(err), { id: "ai-scan", duration: 8000 });
    }
  };

  const updateMultiScan = (i, patch) =>
    setMultiScan((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  // Batch add: new products are created, already-stocked ones get their
  // detected quantity added as a "restocked" movement instead of a duplicate.
  const confirmMultiScan = async () => {
    setMultiBusy(true);
    try {
      let created = 0, restocked = 0;
      for (const r of multiScan.filter((x) => x.include)) {
        const qty = Math.max(0, Number(r.qty) || 0);
        if (r.existing) {
          if (qty > 0) {
            await api.post(`/inventory/${r.existing.id}/movements`, { delta: qty, reason: "restocked", note: "AI photo scan" });
            restocked += 1;
          }
        } else {
          await api.post("/inventory", {
            yacht_id: activeYacht.id, name: r.name, group: r.group || "Other",
            category: r.category || "", brand: r.brand || null, part_number: r.part_number || null,
            quantity: qty, min_stock: 0, expiry_date: r.expiry_date || null,
          });
          created += 1;
        }
      }
      setMultiScan(null);
      toast.success(`${created} product${created !== 1 ? "s" : ""} added${restocked ? `, ${restocked} restocked` : ""}`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setMultiBusy(false);
    }
  };

  // Called by <BarcodeScanner> the moment a barcode/QR code is decoded.
  // We close the scanner, hit OpenFoodFacts, prefill the create form
  // with whatever we got, and (if a product image came back) resize it
  // to 300 px wide and stash it as the photo. Anything we can't fill is
  // simply left blank for the user to type.
  const handleScannedCode = async (code) => {
    setScannerOpen(false);
    // Known product? Same "already in stock" prompt as the AI photo flow.
    const norm = (v) => String(v || "").trim().toLowerCase();
    const existing = items.find((it) => it.part_number && norm(it.part_number) === norm(code));
    if (existing) {
      setDupPrompt({ existing, prefill: { ...DEFAULT, part_number: code } });
      return;
    }
    setEditingId(null);
    // Show the dialog immediately so the user sees something happening
    // even on a slow OFF lookup. Fields populate as the request resolves.
    const initial = { ...DEFAULT, part_number: code };
    setForm(initial);
    setOpen(true);
    toast.message(`Scanned ${code} — looking up product…`);

    let lookup = {};
    try {
      lookup = await lookupBarcode(code);
    } catch {
      lookup = {};
    }

    let photo_data_url = "";
    if (lookup.image_url) {
      try {
        photo_data_url = await resizeImageToDataUrl(lookup.image_url, 300);
      } catch {
        // OFF image fetch failures are non-fatal — keep the rest of the
        // looked-up data and skip the photo.
      }
    }

    setForm((prev) => ({
      ...prev,
      name: prev.name || lookup.name || "",
      brand: prev.brand || lookup.brand || "",
      // Barcode lookups are OpenFoodFacts → food/drink products.
      group: "Food & Drinks",
      category: lookup.category || prev.category,
      photo_data_url: prev.photo_data_url || photo_data_url || "",
    }));

    if (lookup.name) {
      toast.success(`Found: ${lookup.name}${lookup.brand ? ` · ${lookup.brand}` : ""}`);
    } else {
      toast.warning("Product not in OpenFoodFacts — fill the details manually");
    }
  };
  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      name: it.name || "",
      group: it.group || mainCategoryForItem(it),
      category: it.category || "",
      brand: it.brand || "",
      part_number: it.part_number || "",
      quantity: it.quantity ?? 0,
      min_stock: it.min_stock ?? 0,
      location: it.location || "",
      supplier: it.supplier || "",
      supplier_id: it.supplier_id || "",
      price: it.price ?? "",
      notes: it.notes || "",
      expiry_date: (it.expiry_date || "").slice(0, 10),
      photo_data_url: "",
      photo_path: it.photo_path || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        yacht_id: activeYacht.id,
        quantity: Number(form.quantity || 0),
        min_stock: Number(form.min_stock || 0),
        price: form.price === "" ? null : Number(form.price),
        // Empty string → null so the DB stays clean and the
        // optional Pydantic field works as expected.
        supplier_id: form.supplier_id || null,
        supplier: form.supplier || null,
        photo_data_url: form.photo_data_url || null,
        photo_path: form.photo_path || null,
      };
      if (editingId) {
        await api.patch(`/inventory/${editingId}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/inventory", payload);
        toast.success("Item added");
      }
      setForm(DEFAULT);
      setEditingId(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this item? Its stock-movement history will also be lost.")) return;
    try { await api.delete(`/inventory/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  // One-tap "Use 1" — logs a single-unit consumption movement.
  const quickUseBusy = useRef(false);
  const quickUse = async (it) => {
    if (quickUseBusy.current) return;
    quickUseBusy.current = true;
    try {
      const { data } = await api.post(`/inventory/${it.id}/movements`, { delta: -1, reason: "used", note: "" });
      const mvId = data.movement?.id;
      toast.success(`Used 1 — ${it.name}: ${data.new_quantity} left`, mvId ? {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data: d } = await api.delete(`/inventory/movements/${mvId}`);
              toast.success(`Undone — ${it.name}: ${d.new_quantity} left`);
              load();
            } catch (e) { toast.error(fmtErr(e)); }
          },
        },
      } : undefined);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      quickUseBusy.current = false;
    }
  };

  const filtered = items
    .filter((i) =>
      (!q || i.name.toLowerCase().includes(q.toLowerCase()) || (i.part_number || "").toLowerCase().includes(q.toLowerCase())) &&
      (!cat || mainCategoryForItem(i) === cat) &&
      (!typ || (i.category || "") === typ) &&
      (!loc || (i.location || "") === loc) &&
      (!lowStockOnly || (i.quantity ?? 0) <= (i.min_stock ?? 0)) &&
      (!expiringOnly || (expiryDays(i) ?? 99) <= 7)
    )
    .sort((a, b) => {
      // Low-stock items float to the top of every group; the previous
      // category → location → name order applies within each band.
      const lowA = (a.quantity ?? 0) <= (a.min_stock ?? 0) ? 0 : 1;
      const lowB = (b.quantity ?? 0) <= (b.min_stock ?? 0) ? 0 : 1;
      if (lowA !== lowB) return lowA - lowB;
      const ta = (a.category || "").localeCompare(b.category || "");
      if (ta !== 0) return ta;
      const la = (a.location || "").localeCompare(b.location || "");
      if (la !== 0) return la;
      return (a.name || "").localeCompare(b.name || "");
    });

  // Group the filtered items by main category, in the fixed MAIN_CATEGORIES
  // order. Only non-empty groups are rendered. Any unexpected/custom group
  // value lands after the known ones (defensive — the form only allows the
  // fixed list).
  const grouped = useMemo(() => {
    const buckets = new Map(MAIN_CATEGORIES.map((m) => [m, []]));
    for (const it of filtered) {
      const g = mainCategoryForItem(it);
      if (!buckets.has(g)) buckets.set(g, []);
      buckets.get(g).push(it);
    }
    return [...buckets.entries()].filter(([, arr]) => arr.length > 0);
  }, [filtered]);

  // Distinct, non-empty locations seen on this yacht's items (sorted).
  const locations = Array.from(
    new Set(items.map((i) => (i.location || "").trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  // Distinct, non-empty Types (item.category) for the Type filter dropdown.
  // Scoped to the selected main category when one is active, so the list
  // only offers types that actually exist within that category.
  const typeFilterOptions = useMemo(() => {
    const seen = items
      .filter((i) => !cat || mainCategoryForItem(i) === cat)
      .map((i) => (i.category || "").trim())
      .filter(Boolean);
    return Array.from(new Set(seen)).sort((a, b) => a.localeCompare(b));
  }, [items, cat]);

  // Type options for the form, scoped to the selected main category: the
  // predefined types for that group merged with any custom types already used
  // by items in that group. Crew can still add a brand-new type on the fly.
  const typeOptions = useMemo(() => {
    const preset = TYPES_BY_CATEGORY[form.group] || [];
    const fromItems = items
      .filter((i) => mainCategoryForItem(i) === form.group)
      .map((i) => (i.category || "").trim())
      .filter(Boolean);
    return Array.from(new Set([...preset, ...fromItems])).sort((a, b) => a.localeCompare(b));
  }, [items, form.group]);

  if (!activeYacht) return <EmptyYacht />;

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={activeYacht.name}
        title={t("inventory.title")}
        right={
          can("inventory:create") || can("inventory:edit") ? (
          <div className="flex items-center gap-2">
            <TourReplayButton tourId="add-inventory" />
            <Button
              variant="outline"
              className="px-2"
              onClick={() => setScannerOpen(true)}
              data-testid="scan-inv-btn"
              title="Scan a product barcode or QR code"
            >
              <ScanLine className="h-4 w-4 mr-1.5" />Scan
            </Button>
            <input
              ref={aiPhotoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleAiPhoto}
              data-testid="ai-photo-input"
            />
            <Button
              variant="outline"
              className="px-2"
              onClick={() => aiPhotoInputRef.current?.click()}
              data-testid="ai-photo-btn"
              title="Photograph an item — AI identifies it, you only add quantity & location"
            >
              <Camera className="h-4 w-4 mr-1.5" />AI Photo
            </Button>
          <Dialog
            open={open}
            onOpenChange={(o) => {
              setOpen(o);
              if (!o) { setEditingId(null); setForm(DEFAULT); }
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-ink hover:bg-black"
                onClick={openCreate}
                data-testid="add-inv-btn"
                aria-label={t("inventory.add_item")}
                title={t("inventory.add_item")}
                size="icon"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent
              className="max-w-2xl max-h-[90vh] overflow-y-auto"
              onPointerDownOutside={(e) => { if (activeTour) e.preventDefault(); }}
              onInteractOutside={(e) => { if (activeTour) e.preventDefault(); }}
              onEscapeKeyDown={(e) => { if (activeTour) e.preventDefault(); }}
              onOpenAutoFocus={(e) => { if (activeTour) e.preventDefault(); }}
            >
              <DialogHeader>
                <DialogTitle>{editingId ? t("inventory.edit_item") : t("inventory.add_item")}</DialogTitle>
                <DialogDescription className="text-[12px]">
                  Stock changes (use, breakage, restock) are tracked separately via the <strong>Adjust</strong> action on each row — that way you keep an audit trail instead of just overwriting the count here.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-tour="inv-fields">
                <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="inv-name" /></Field>
                <Field label="Main category *">
                  <select
                    className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                    value={form.group}
                    onChange={(e) => setForm({ ...form, group: e.target.value, category: "" })}
                    data-testid="inv-group"
                  >
                    {MAIN_CATEGORIES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Type">
                  <EditableSelect
                    value={form.category}
                    options={typeOptions}
                    onChange={(v) => setForm({ ...form, category: v })}
                    placeholder="Select or add type"
                    addLabel="+ New type…"
                    promptText="New type name"
                    testid="inv-category"
                  />
                </Field>
                <Field label="Brand"><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></Field>
                <Field label="Part number"><Input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} /></Field>
                <Field label="Quantity"><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="inv-qty" /></Field>
                <Field label="Min stock"><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="inv-min" /></Field>
                <Field label="Location">
                  <EditableSelect
                    value={form.location}
                    options={locations}
                    onChange={(v) => setForm({ ...form, location: v })}
                    placeholder="Select or add location"
                    addLabel="+ New location…"
                    promptText="New location"
                    testid="inv-location"
                  />
                </Field>
                <Field label="Supplier">
                  <SupplierSelect
                    suppliers={suppliers}
                    value={{ supplier_id: form.supplier_id, supplier: form.supplier }}
                    onChange={({ supplier_id, supplier }) => setForm({ ...form, supplier_id, supplier })}
                  />
                </Field>
                <Field label="Price"><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
                <Field label="Expiry date"><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
                <Field label="Notes" className="md:col-span-2"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                <Field label="Photo" className="md:col-span-2">
                  <PhotoPicker
                    value={form.photo_data_url}
                    existingPath={form.photo_path}
                    onChange={(v) => setForm((f) => ({ ...f, photo_data_url: v, photo_path: v === "" ? "" : f.photo_path }))}
                  />
                </Field>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-ink hover:bg-black" disabled={!form.name} data-testid="save-inv">
                  {editingId ? "Save changes" : "Add item"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
          ) : null
        }
      />

      <div className="mt-6 flex items-center gap-1 border-b border-zinc-200" data-testid="inv-tabs">
        <TabBtn active={view === "items"} onClick={() => setView("items")} testid="tab-items">
          Items
        </TabBtn>
        {manageValuesKind && (
        <ManageTypesDialog
          yachtId={activeYacht.id}
          kind={manageValuesKind}
          onClose={() => setManageValuesKind(null)}
          onChanged={load}
        />
      )}
      {can("inventory:consumption") && (
          <TabBtn active={view === "consumption"} onClick={() => setView("consumption")} testid="tab-consumption">
            Consumption
          </TabBtn>
        )}
      </div>

      {view === "consumption" ? (
        <InventoryConsumption yacht={activeYacht} />
      ) : (
        <>
      <div className="mt-6 sm:mt-8 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center" data-testid="inv-filters">
        <Input
          placeholder="Search by name or part #"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full sm:max-w-xs"
          data-testid="inv-search"
        />
        <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2">
          <select className="h-9 rounded-md border border-input px-3 text-sm bg-white" value={cat} onChange={(e) => { setCat(e.target.value); setTyp(""); }} data-testid="inv-cat-filter">
            <option value="">All categories</option>
            {MAIN_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className="h-9 rounded-md border border-input px-3 text-sm bg-white" value={typ} onChange={(e) => setTyp(e.target.value)} data-testid="inv-type-filter" disabled={typeFilterOptions.length === 0}>
            <option value="">All types</option>
            {typeFilterOptions.map((tp) => <option key={tp}>{tp}</option>)}
          </select>
          {can("inventory:edit") && (
            <button
              type="button"
              onClick={() => setManageValuesKind("types")}
              className="inline-flex items-center gap-1.5 h-9 rounded-md border border-zinc-200 bg-white px-3 text-xs text-zinc-600 hover:bg-zinc-50"
              data-testid="manage-types-btn"
            >
              <Pencil className="h-3.5 w-3.5" />Manage types
            </button>
          )}
          <select className="h-9 rounded-md border border-input px-3 text-sm bg-white" value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="inv-loc-filter" disabled={locations.length === 0}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l}>{l}</option>)}
          </select>
          {can("inventory:edit") && (
            <button
              type="button"
              onClick={() => setManageValuesKind("locations")}
              className="inline-flex items-center gap-1.5 h-9 rounded-md border border-zinc-200 bg-white px-3 text-xs text-zinc-600 hover:bg-zinc-50"
              data-testid="manage-locations-btn"
            >
              <Pencil className="h-3.5 w-3.5" />Manage locations
            </button>
          )}
          {lowStockOnly && (
            <button
              type="button"
              onClick={() => setLowStockOnly(false)}
              className="inline-flex items-center gap-1.5 h-9 rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-3 text-xs col-span-2 justify-center sm:justify-start"
              data-testid="inv-low-stock-chip"
            >
              Low stock only
              <span aria-hidden className="text-amber-500">×</span>
            </button>
          )}
          {expiringOnly && (
            <button
              type="button"
              onClick={() => setExpiringOnly(false)}
              className="inline-flex items-center gap-1.5 h-9 rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-3 text-xs col-span-2 justify-center sm:justify-start"
              data-testid="inv-expiring-chip"
            >
              Expiring soon only
              <span aria-hidden className="text-amber-500">×</span>
            </button>
          )}
        </div>
      </div>

      {/* Grouped, collapsible sections per main category. Each header shows
          the item count and a low-stock badge so the crew can spot the
          buckets that need attention without expanding everything. */}
      <div className="mt-4 space-y-3" data-testid="inv-groups">
        {filtered.length === 0 && (
          <div className="rounded-md border border-zinc-200 bg-white p-10 text-center text-zinc-500" data-testid="inv-empty">
            <Package className="h-6 w-6 mx-auto text-zinc-300" />
            <div className="mt-2 text-sm">No inventory items.</div>
          </div>
        )}
        {grouped.map(([group, list]) => {
          const filterActive = !!(q.trim() || cat || typ || loc || lowStockOnly || expiringOnly);
          const isCollapsed = !filterActive && !expandedGroups.has(group);
          const lowCount = list.filter((it) => (it.quantity ?? 0) <= (it.min_stock ?? 0)).length;
          const expCount = list.filter((it) => (expiryDays(it) ?? 99) <= 7).length;
          const onOpen = (it) => { if (can("inventory:movement")) setAdjustItem(it); else setHistoryItem(it); };
          const onQuickUse = can("inventory:movement") ? quickUse : null;
          return (
            <div key={group} className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid={`inv-group-${group}`}>
              <div className="w-full flex items-center gap-2 px-4 py-3 bg-zinc-50 hover:bg-zinc-100 transition-colors">
                <button
                  type="button"
                  onClick={() => toggleGroup(group)}
                  className="flex-1 flex items-center gap-2 text-left min-w-0"
                  data-testid={`inv-group-header-${group}`}
                  aria-expanded={!isCollapsed}
                >
                  <span className="flex items-center gap-2 font-medium text-sm text-zinc-800 min-w-0">
                    <ChevronRight className={`h-4 w-4 text-zinc-400 shrink-0 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                    <span className="truncate">{group}</span>
                    <span className="text-zinc-400 font-normal">({list.length})</span>
                  </span>
                </button>
                {lowCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0" data-testid={`inv-group-low-${group}`}>
                    <AlertCircle className="h-3 w-3" /> {lowCount} low
                  </span>
                )}
                {(lowCount > 0 || expCount > 0) && (can("inventory:create") || can("inventory:edit")) && (
                  <button
                    type="button"
                    onClick={() => setReorderGroup(group)}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-ink bg-white border border-zinc-300 rounded-md px-2 py-1 hover:bg-zinc-50 shrink-0"
                    data-testid={`inv-reorder-btn-${group}`}
                    title="Build a reorder list for the low-stock and expiring items in this category"
                  >
                    <ClipboardList className="h-3.5 w-3.5" /> Reorder
                  </button>
                )}
              </div>
              {!isCollapsed && (
                <>
                  {/* MOBILE — cards */}
                  <ul className="grid grid-cols-1 gap-2 p-2 md:hidden" data-testid={`inv-cards-${group}`}>
                    {list.map((it) => <InvCard key={it.id} it={it} onOpen={onOpen} onQuickUse={onQuickUse} />)}
                  </ul>
                  {/* DESKTOP — table */}
                  <div className="hidden md:block">
                    <table className="w-full text-sm table-fixed" data-testid={`inv-table-${group}`}>
                      <colgroup>
                        <col className="w-[28%]" />
                        <col className="w-[20%]" />
                        <col className="w-[84px]" />
                        <col className="w-[72px]" />
                        <col className="w-[72px]" />
                        <col className="w-[16%]" />
                        <col className="w-[76px]" />
                      </colgroup>
                      <thead className="bg-white text-zinc-500 text-xs uppercase tracking-wider border-b border-zinc-200">
                        <tr>
                          <Th>Item</Th><Th>Type</Th><Th>Photo</Th>
                          <Th className="text-right">Qty</Th><Th className="text-right">Min</Th>
                          <Th>Location</Th><Th />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-200">
                        {list.map((it) => <InvRow key={it.id} it={it} onOpen={onOpen} onQuickUse={onQuickUse} />)}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
        </>
      )}

      {/* AI photo → product already in stock */}
      <Dialog open={!!dupPrompt} onOpenChange={(o) => { if (!o) setDupPrompt(null); }}>
        <DialogContent className="max-w-md" data-testid="ai-duplicate-dialog">
          <DialogHeader>
            <DialogTitle>Already in stock</DialogTitle>
            <DialogDescription>
              You already have this product in your inventory.
            </DialogDescription>
          </DialogHeader>
          {dupPrompt && (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm" data-testid="ai-duplicate-existing">
              <div className="font-medium">{dupPrompt.existing.name}</div>
              <div className="text-zinc-500 text-xs mt-0.5">
                Quantity: {Number(dupPrompt.existing.quantity ?? 0)}
                {dupPrompt.existing.location ? ` · ${dupPrompt.existing.location}` : ""}
                {dupPrompt.existing.part_number ? ` · ${dupPrompt.existing.part_number}` : ""}
              </div>
            </div>
          )}
          {dupPrompt?.detectedQty > 1 && (
            <div className="text-sm text-amber-700" data-testid="ai-duplicate-counted">
              AI counted {dupPrompt.detectedQty} pieces in the photo.
            </div>
          )}
          <div className="text-sm text-zinc-600">Do you want to add more quantity, or add it as a new product?</div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => {
              const p = dupPrompt; setDupPrompt(null);
              setEditingId(null); setForm(p.prefill); setOpen(true);
            }} data-testid="ai-duplicate-new-product">
              Add as new product
            </Button>
            <Button className="bg-ink hover:bg-black" onClick={() => {
              const it = dupPrompt.existing; setDupPrompt(null); setAdjustItem(it);
            }} data-testid="ai-duplicate-add-qty">
              Add quantity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MultiScanDialog
        rows={multiScan}
        busy={multiBusy}
        onChange={updateMultiScan}
        onClose={() => setMultiScan(null)}
        onConfirm={confirmMultiScan}
      />

      <AdjustDialog
        item={adjustItem}
        canEdit={can("inventory:edit")}
        onClose={() => setAdjustItem(null)}
        onSaved={() => { setAdjustItem(null); load(); }}
        onShowHistory={() => { const it = adjustItem; setAdjustItem(null); setHistoryItem(it); }}
        onEditDetails={() => { const it = adjustItem; setAdjustItem(null); openEdit(it); }}
        onShowDetails={() => { const it = adjustItem; setAdjustItem(null); setDetailItem(it); }}
      />
      <HistoryDialog
        item={historyItem}
        onClose={() => setHistoryItem(null)}
      />
      <DetailsDialog
        item={detailItem}
        canEdit={can("inventory:edit")}
        canDelete={can("inventory:delete")}
        onClose={() => setDetailItem(null)}
        onEdit={() => { const it = detailItem; setDetailItem(null); openEdit(it); }}
        onDelete={async () => {
          const it = detailItem;
          if (!it) return;
          setDetailItem(null);
          await del(it.id);
        }}
      />
      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={handleScannedCode}
      />
      <ReorderDialog
        group={reorderGroup}
        items={items}
        suppliers={suppliers}
        yachtName={activeYacht?.name}
        onClose={() => setReorderGroup(null)}
      />
    </div>
  );
}


/* ─── Stock-movement dialog ────────────────────────────────────────────
 * Lets the user record a single adjustment to an item's quantity. The
 * `reason` drives the sign convention so the user doesn't have to think
 * about positive/negative — Used and Broken count as outgoing, Restocked
 * as incoming, and Manual adjustment lets you enter either.
 */
function AdjustDialog({ item, canEdit, onClose, onSaved, onShowHistory, onEditDetails, onShowDetails }) {
  const [reason, setReason] = useState("used");
  const [qty, setQty] = useState("1");
  const [delta, setDelta] = useState("");      // only for manual adjust
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(false);
  const photoSrc = useItemPhotoUrl(item);

  useEffect(() => {
    if (item) {
      setReason("used"); setQty("1"); setDelta(""); setNote("");
    }
  }, [item?.id]);  // eslint-disable-line

  if (!item) return null;

  const submit = async () => {
    setBusy(true);
    try {
      let d;
      if (reason === "adjusted") {
        d = Number(delta);
        if (Number.isNaN(d) || d === 0) throw new Error("Enter a non-zero delta.");
      } else {
        const n = Number(qty);
        if (Number.isNaN(n) || n <= 0) throw new Error("Quantity must be greater than zero.");
        d = (reason === "restocked") ? n : -n;
      }
      const { data } = await api.post(`/inventory/${item.id}/movements`, {
        delta: d, reason, note,
      });
      toast.success(`Updated — new stock: ${data.new_quantity}`);
      onSaved();
    } catch (e) {
      toast.error(e?.message || fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto p-4 sm:p-6"
        data-testid="adjust-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg pr-6 break-words">
            Adjust stock — {item.name}
          </DialogTitle>
          <DialogDescription className="text-[12px] sm:text-sm">
            Current stock: <strong>{item.quantity}</strong>. Pick what happened and the change will be logged with your name + timestamp.
          </DialogDescription>
        </DialogHeader>

        {/* Item snapshot — large photo + metadata so the user has visual
            confirmation they're acting on the right item. Image is hidden
            when the item has none (no awkward empty box). */}
        {(photoSrc || item.brand || item.part_number || item.location) && (
          <div className="flex items-start gap-3 rounded-md border border-zinc-200 bg-zinc-50 p-3">
            {photoSrc && (
              <img
                src={photoSrc}
                alt={item.name}
                className="rounded-md border border-zinc-200 object-cover shrink-0 cursor-zoom-in"
                style={{ width: 112, height: 112 }}
                title="Click to zoom"
                onClick={() => setZoom(true)}
                data-testid="adjust-photo"
              />
            )}
            <div className="min-w-0 flex-1 text-[12px] text-zinc-600 space-y-0.5">
              {item.category && <div><span className="text-zinc-400">Category </span>{item.category}</div>}
              {item.brand && <div><span className="text-zinc-400">Brand </span>{item.brand}</div>}
              {item.part_number && <div><span className="text-zinc-400">Part # </span><span className="font-mono">{item.part_number}</span></div>}
              {item.location && <div><span className="text-zinc-400">Location </span>{item.location}</div>}
              <div>
                <span className="text-zinc-400">Min stock </span>
                <span className="font-mono">{item.min_stock ?? 0}</span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {[
            ["used", "Used", MinusCircle, "Items consumed during work."],
            ["broken", "Broken / lost", MinusCircle, "Items damaged or missing."],
            ["restocked", "Restocked", PlusCircle, "New items received."],
            ["adjusted", "Manual adjustment", Wrench, "Direct count correction."],
          ].map(([val, label, Icon, desc]) => (
            <button
              key={val}
              type="button"
              onClick={() => setReason(val)}
              className={`text-left rounded-md border px-3 py-2 transition-colors ${reason === val ? "border-ink bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`}
              data-testid={`reason-${val}`}
            >
              <div className="flex items-center gap-1.5 text-[13px] font-medium">
                <Icon className="h-3.5 w-3.5" />
                {label}
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{desc}</div>
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {reason === "adjusted" ? (
            <div>
              <Label className="text-[12px]">Delta (use negative for stock down)</Label>
              <Input
                type="number"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                placeholder="e.g. -3 or 5"
                data-testid="adjust-delta"
              />
            </div>
          ) : (
            <div>
              <Label className="text-[12px]">Quantity</Label>
              <Input
                type="number"
                min="0"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                data-testid="adjust-qty"
              />
            </div>
          )}
          <div>
            <Label className="text-[12px]">Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. used during engine service"
              data-testid="adjust-note"
            />
          </div>
        </div>

        {/* Secondary affordances — keep History and Edit-details reachable
            from inside the Adjust dialog so the row itself can stay clean
            (clicking the row is what opened this dialog in the first
            place). */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-3 border-t border-zinc-100">
          <button
            type="button"
            onClick={onShowHistory}
            className="text-[12px] text-zinc-600 hover:text-ink underline-offset-2 hover:underline inline-flex items-center gap-1"
            data-testid="adjust-show-history"
          >
            <History className="h-3.5 w-3.5" />
            Show history
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={onEditDetails}
              className="text-[12px] text-zinc-600 hover:text-ink underline-offset-2 hover:underline inline-flex items-center gap-1"
              data-testid="adjust-edit-details"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit details
            </button>
          )}
          <button
            type="button"
            onClick={onShowDetails}
            className="text-[12px] text-zinc-600 hover:text-ink underline-offset-2 hover:underline inline-flex items-center gap-1"
            data-testid="adjust-show-details"
          >
            <Info className="h-3.5 w-3.5" />
            Details
          </button>
        </div>

        <PhotoZoom open={zoom} src={photoSrc} alt={item.name} onClose={() => setZoom(false)} testid="adjust-photo-zoom" />

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="w-full sm:w-auto bg-ink hover:bg-black"
            data-testid="adjust-submit"
          >
            {busy ? "Saving…" : "Record movement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Movement history dialog ───────────────────────────────────────── */
function HistoryDialog({ item, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pull both data sources in parallel and merge into a single
  // chronological timeline. inventory_movements is the legacy stock-
  // change feed; audit_log carries create/edit/delete events plus the
  // new ``stock:*`` shadow rows so non-stock changes (rename, supplier
  // swap, photo replace …) also appear here.
  useEffect(() => {
    if (!item) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/inventory/${item.id}/movements`).then(r => r.data).catch(() => []),
      api.get(`/audit?ref_kind=inventory&ref_id=${item.id}`).then(r => r.data).catch(() => []),
    ]).then(([mvs, audits]) => {
      if (cancelled) return;
      // De-dupe: every stock movement also produces a ``stock:*`` audit
      // row. We prefer the rich movement row (signed delta + reason + note)
      // for those, so drop their audit twins from the merged list.
      const auditNonStock = audits.filter((a) => !String(a.action || "").startsWith("stock:"));
      const merged = [
        ...mvs.map((m) => ({ kind: "movement", ts: m.created_at, data: m })),
        ...auditNonStock.map((a) => ({ kind: "audit", ts: a.created_at, data: a })),
      ].sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
      setRows(merged);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [item?.id]);

  if (!item) return null;
  const fmtDate = (iso) => new Date(iso).toLocaleString();

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle>History — {item.name}</DialogTitle>
          <DialogDescription>
            Most recent change first. Current stock is <strong>{item.quantity}</strong>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-zinc-500 py-6 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-zinc-200 bg-zinc-50/50 px-3 py-6 text-center text-[12px] text-zinc-500">
            Nothing here yet. Edits, deletions and stock adjustments will all appear in this timeline.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100 -mx-6 px-6">
            {rows.map((row) => row.kind === "movement" ? (
              <div key={row.data.id} className="py-2 text-[13px]" data-testid={`history-row-${row.data.id}`}>
                <div className="flex items-baseline gap-2">
                  <span className={`font-mono font-semibold ${row.data.delta < 0 ? "text-red-600" : "text-emerald-600"}`}>
                    {row.data.delta > 0 ? "+" : ""}{row.data.delta}
                  </span>
                  <span className="text-zinc-700">{REASON_LABELS[row.data.reason] || row.data.reason}</span>
                  <span className="ml-auto text-[11px] text-zinc-500">{fmtDate(row.data.created_at)}</span>
                </div>
                <div className="text-[11px] text-zinc-500">
                  by {row.data.by_user_name}{row.data.note ? <> — <span className="italic">{row.data.note}</span></> : null}
                </div>
              </div>
            ) : (
              <AuditRow key={row.data.id} entry={row.data} fmtDate={fmtDate} />
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Audit row ────────────────────────────────────────────────────────
 * Renders a non-stock event from the audit_log: create / update / delete.
 * The backend stamps ``details.changes = {field: {from, to}}`` on updates,
 * which we render as a compact bullet list so users can see exactly what
 * was edited without opening the full record.
 */
function AuditRow({ entry, fmtDate }) {
  const action = (entry.action || "").toLowerCase();
  const tone = action === "deleted"
    ? "text-red-600"
    : action === "created"
    ? "text-emerald-600"
    : "text-zinc-700";
  const changes = entry?.details?.changes || null;
  return (
    <div className="py-2 text-[13px]" data-testid={`audit-row-${entry.id}`}>
      <div className="flex items-baseline gap-2">
        <span className={`font-medium uppercase tracking-wider text-[10px] ${tone}`}>{action}</span>
        <span className="text-zinc-700">{entry.summary}</span>
        <span className="ml-auto text-[11px] text-zinc-500">{fmtDate(entry.created_at)}</span>
      </div>
      <div className="text-[11px] text-zinc-500">by {entry.by_user_name}</div>
      {changes && Object.keys(changes).length > 0 && (
        <ul className="mt-1 ml-2 space-y-0.5 text-[11px] text-zinc-600">
          {Object.entries(changes).map(([k, v]) => (
            <li key={k} className="flex flex-wrap gap-1">
              <span className="text-zinc-500">{k}:</span>
              <span className="line-through text-zinc-400">{String(v.from ?? "—")}</span>
              <span className="text-zinc-400">→</span>
              <span>{String(v.to ?? "—")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/* ─── Barcode / QR scanner ────────────────────────────────────────────
 * Camera viewfinder powered by html5-qrcode.
 *
 * Decode reliability (2026-06-12 fixes after "doesn't read" report):
 *  - useBarCodeDetectorIfSupported → native BarcodeDetector on
 *    Chrome/Android (far better at 1D EAN/UPC than the JS decoder).
 *  - 720p video + continuous autofocus hint — default low-res feeds are
 *    too blurry for product barcodes, especially on iPhones.
 *  - Wide qrbox (90% × 50%) — small boxes miss long 1D codes.
 *  - Explicit format list incl. EAN/UPC/Code128/ITF.
 *  - "Scan from photo" fallback: the native camera app focuses properly,
 *    then we decode the still image — works even where live decoding
 *    fails (common on iOS Safari).
 *
 * Permission handling: getUserMedia denial throws inside `start()` → we
 * toast a friendly hint. Cleaning up `stop()` can throw if never started
 * (open → instant close); the try/catch is intentional.
 */
const SCAN_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
];

function BarcodeScanner({ open, onClose, onDetected }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const stoppedRef = useRef(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    stoppedRef.current = false;
    let cancelled = false;

    (async () => {
      const id = "inv-scanner-region";
      // Wait one tick so the <div> is in the DOM before html5-qrcode
      // tries to read its dimensions.
      await new Promise((r) => requestAnimationFrame(r));
      if (cancelled) return;
      try {
        const inst = new Html5Qrcode(id, {
          verbose: false,
          formatsToSupport: SCAN_FORMATS,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        });
        scannerRef.current = inst;
        await inst.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (vw, vh) => ({
              width: Math.floor(vw * 0.9),
              height: Math.floor(vh * 0.5),
            }),
            videoConstraints: {
              facingMode: "environment",
              width: { ideal: 1280 },
              height: { ideal: 720 },
              advanced: [{ focusMode: "continuous" }],
            },
          },
          async (decoded) => {
            if (stoppedRef.current) return;
            stoppedRef.current = true;
            try { await inst.stop(); } catch {}
            try { await inst.clear(); } catch {}
            onDetected(decoded);
          },
          /* onError */ () => {},   // per-frame decode failures are noisy; ignore
        );
      } catch (e) {
        // No camera / permission denied: keep the dialog open — the
        // "Scan from photo" fallback still works without a live feed.
        toast.error("Live camera unavailable — use \"Scan from photo\" instead.");
      }
    })();

    return () => {
      cancelled = true;
      const inst = scannerRef.current;
      if (inst) {
        (async () => {
          try { if (!stoppedRef.current) await inst.stop(); } catch {}
          try { await inst.clear(); } catch {}
        })();
      }
    };
  }, [open, onDetected, onClose]);

  // Fallback path: decode a photo taken with the native camera app.
  const scanPhoto = async (file) => {
    if (!file) return;
    const live = scannerRef.current;
    if (live) {
      stoppedRef.current = true;
      try { await live.stop(); } catch {}
      try { await live.clear(); } catch {}
    }
    try {
      const inst = new Html5Qrcode("inv-scanner-file-region", {
        verbose: false,
        formatsToSupport: SCAN_FORMATS,
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      });
      const decoded = await inst.scanFileV2(file, /* showImage */ false);
      try { inst.clear(); } catch {}
      onDetected(decoded?.decodedText ?? decoded);
    } catch {
      toast.error("No barcode found in that photo — try again closer and in good light.");
      onClose();
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <ScanLine className="h-5 w-5" /> Scan barcode or QR
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Point your rear camera at the product barcode. We&rsquo;ll look it up and pre-fill the form.
            If it won&rsquo;t read, use &ldquo;Scan from photo&rdquo; below.
          </DialogDescription>
        </DialogHeader>
        <div
          id="inv-scanner-region"
          ref={containerRef}
          className="rounded-md overflow-hidden bg-black aspect-video"
          data-testid="scanner-region"
        />
        <div id="inv-scanner-file-region" className="hidden" />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => scanPhoto(e.target.files?.[0])}
          data-testid="scanner-file-input"
        />
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="scanner-photo-btn">
            <Camera className="h-4 w-4 mr-2" />Scan from photo
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="scanner-close">
            <X className="h-4 w-4 mr-2" />Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Photo picker ─────────────────────────────────────────────────────
 * File-input wrapper that resizes the chosen image to 300px wide (height
 * proportional) and emits the resulting base64 JPEG data URL via
 * `onChange`. We keep the actual <input type="file"> hidden and drive it
 * from a styled button so the form looks consistent with the rest of the
 * dialog. Errors during decode are surfaced via toast — bad / huge files
 * just leave the existing photo untouched.
 */
/* Multi-product AI scan review: several DIFFERENT products were detected in
 * one photo. The user can untick rows, tweak quantities and add everything in
 * one go; rows matching existing stock are restocked instead of duplicated. */
function MultiScanDialog({ rows, busy, onChange, onClose, onConfirm }) {
  if (!rows) return null;
  const included = rows.filter((r) => r.include);
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="multi-scan-dialog">
        <DialogHeader>
          <DialogTitle>{rows.length} products detected</DialogTitle>
          <DialogDescription>Review what the AI found, adjust quantities and add everything in one go.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {rows.map((r, i) => (
            <div
              key={i}
              className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${r.include ? "border-zinc-300 bg-white" : "border-zinc-200 bg-zinc-50 opacity-60"}`}
              data-testid={`multi-scan-row-${i}`}
            >
              <input
                type="checkbox"
                checked={r.include}
                onChange={(e) => onChange(i, { include: e.target.checked })}
                className="mt-1 h-4 w-4 accent-black cursor-pointer"
                data-testid={`multi-scan-include-${i}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium break-words">{r.name}</div>
                <div className="text-xs text-zinc-500">
                  {[r.brand, r.group, r.category].filter(Boolean).join(" · ")}
                  {r.expiry_date ? ` · expires ${r.expiry_date}` : ""}
                </div>
                {r.existing && (
                  <div className="text-[11px] text-amber-700 mt-0.5" data-testid={`multi-scan-existing-${i}`}>
                    Already in stock ({Number(r.existing.quantity ?? 0)}) — quantity will be added to it
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-[11px] text-zinc-500">Qty</span>
                <Input
                  type="number" min="0" value={r.qty}
                  onChange={(e) => onChange(i, { qty: e.target.value })}
                  className="h-8 w-16 text-right"
                  data-testid={`multi-scan-qty-${i}`}
                />
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="multi-scan-cancel">Cancel</Button>
          <Button className="bg-ink hover:bg-black" onClick={onConfirm} disabled={busy || included.length === 0} data-testid="multi-scan-confirm">
            {busy ? "Adding…" : `Add ${included.length} item${included.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* Shelf audit review: what the AI counted on the shelf vs. recorded stock.
 * Matches are informational; mismatches offer a one-tick quantity correction;
 * unknown products can be added with the counted quantity. */
function PhotoPicker({ value, existingPath, onChange }) {
  const [busy, setBusy] = useState(false);
  const hasPhoto = !!(value || existingPath);
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      e.target.value = "";
      return;
    }
    try {
      setBusy(true);
      const dataUrl = await resizeImageToDataUrl(f, 300);
      onChange(dataUrl);
    } catch (err) {
      toast.error("Could not read that image");
    } finally {
      setBusy(false);
      e.target.value = "";  // allow re-uploading the same file
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid="inv-photo-picker">
      {value ? (
        <img
          src={value}
          alt=""
          className="rounded-md border border-zinc-200 object-cover"
          style={{ width: 60, height: 60 }}
          data-testid="inv-photo-preview"
        />
      ) : existingPath ? (
        <AuthImage path={existingPath} className="rounded-md border border-zinc-200 object-cover w-[60px] h-[60px]" />
      ) : (
        <div className="rounded-md border border-dashed border-zinc-300 flex items-center justify-center text-[10px] text-zinc-400" style={{ width: 60, height: 60 }}>
          No photo
        </div>
      )}
      <div className="flex flex-col gap-1.5 items-start">
        <label className="inline-flex items-center gap-2 px-3 h-8 rounded-md border border-zinc-300 bg-white text-sm cursor-pointer hover:bg-zinc-50">
          <ImagePlusIcon /> {hasPhoto ? "Replace photo" : "Add photo"}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={busy}
            onChange={handleFile}
            data-testid="inv-photo-input"
          />
        </label>
        {hasPhoto && (
          <button
            type="button"
            className="text-[12px] text-red-600 hover:underline"
            onClick={() => onChange("")}
            data-testid="inv-photo-clear"
          >
            Remove
          </button>
        )}
        <div className="text-[11px] text-zinc-400">Auto-resized to 300 px wide.</div>
      </div>
    </div>
  );
}

// Tiny inline icon — keeps the bundle from importing another lucide entry
// just for one button. The shape mirrors the lucide ImagePlus glyph.
function ImagePlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="M21 15l-5-5L5 21" />
      <path d="M14 4h6m-3-3v6" />
    </svg>
  );
}


/* ─── Photo zoom overlay ──────────────────────────────────────────────
 * Full-screen dark-backdrop zoom for the item photo (data-URL thumbnails,
 * so the shared ImageLightbox — which fetches by storage path — doesn't
 * apply). Click the image, the X or the backdrop / Escape to close.
 */
function PhotoZoom({ open, src, alt, onClose, testid }) {
  if (!open || !src) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="w-[100vw] h-[100dvh] max-w-none p-0 bg-zinc-950/95 border-0 rounded-none sm:rounded-none flex items-center justify-center overflow-hidden"
        data-testid={testid}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-30 rounded-full bg-black/60 hover:bg-black/80 text-white p-2"
          aria-label="Close"
          data-testid={`${testid}-close`}
        >
          <X className="h-5 w-5" />
        </button>
        <img
          src={src}
          alt={alt || ""}
          className="max-h-[100dvh] max-w-[100vw] w-auto h-auto object-contain cursor-zoom-out"
          onClick={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}


/* ─── Details dialog ──────────────────────────────────────────────────
 * Shows every field on an inventory item and exposes Edit / Delete from
 * inside the modal so the row itself stays clean (just Adjust · History ·
 * Info). Edit closes this dialog and opens the existing create/edit form
 * pre-populated with the item; Delete confirms then closes both.
 */
function DetailsDialog({ item, canEdit, canDelete, onClose, onEdit, onDelete }) {
  const [zoom, setZoom] = useState(false);
  const photoSrc = useItemPhotoUrl(item);
  useEffect(() => { setZoom(false); }, [item]);
  if (!item) return null;
  const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : null);
  const supplierLabel = item.supplier
    ? (item.supplier_id ? `${item.supplier} (linked)` : `${item.supplier} (other)`)
    : null;

  // Field rows are conditionally rendered so missing optional values
  // don't show as ugly "—" lines; the user just sees what's actually set.
  const rows = [
    ["Category",     item.category || null],
    ["Brand",        item.brand || null],
    ["Part number",  item.part_number || null],
    ["Quantity",     `${item.quantity ?? 0}`],
    ["Min stock",    `${item.min_stock ?? 0}`],
    ["Location",     item.location || null],
    ["Supplier",     supplierLabel],
    ["Price",        item.price != null && item.price !== "" ? String(item.price) : null],
    ["Expiry date",  fmtDate(item.expiry_date)],
    ["Notes",        item.notes || null],
  ].filter(([, v]) => v != null && v !== "");

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{item.name}</DialogTitle>
          <DialogDescription className="text-[12px]">
            Inventory item details. Stock changes and history are tracked separately.
          </DialogDescription>
        </DialogHeader>

        {photoSrc && (
          <div className="flex justify-center -mt-1 -mb-1">
            <img
              src={photoSrc}
              alt=""
              className="rounded-md border border-zinc-200 max-h-48 object-contain cursor-zoom-in"
              title="Click to zoom"
              onClick={() => setZoom(true)}
              data-testid="details-photo"
            />
          </div>
        )}

        {/* Full-screen zoom of the item photo — click photo to open, X /
            Escape / backdrop click to close. */}
        <PhotoZoom open={zoom} src={photoSrc} alt={item.name} onClose={() => setZoom(false)} testid="details-photo-zoom" />

        <dl className="divide-y divide-zinc-100 -mx-6 px-6 text-sm" data-testid="details-rows">
          {rows.map(([label, value]) => (
            <div key={label} className="grid grid-cols-3 gap-3 py-2">
              <dt className="text-[12px] uppercase tracking-wider text-zinc-500">{label}</dt>
              <dd className="col-span-2 text-zinc-800 break-words">{value}</dd>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="py-6 text-center text-[12px] text-zinc-500">
              No additional details set on this item.
            </div>
          )}
        </dl>

        <DialogFooter className="flex flex-row-reverse sm:flex-row sm:justify-between gap-2">
          <Button variant="outline" onClick={onClose} data-testid="details-close">Close</Button>
          <div className="flex flex-1 justify-end gap-2">
            {canDelete && (
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={onDelete}
                data-testid="details-delete"
              >
                <Trash2 className="h-4 w-4 mr-2" />Delete
              </Button>
            )}
            {canEdit && (
              <Button
                onClick={onEdit}
                className="bg-ink hover:bg-black"
                data-testid="details-edit"
              >
                <Pencil className="h-4 w-4 mr-2" />Edit
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Editable select ──────────────────────────────────────────────────
 * Native dropdown that doubles as a "type your own value" input. When the
 * user picks "+ New …" the row morphs into a small inline form (text +
 * Save / Cancel) and persists the new value back into the parent state
 * exactly the way a free-form `<input>` would. We use a sentinel value
 * `__add__` for the special option so we can distinguish "select an
 * existing entry" from "open the inline editor" without state plumbing.
 *
 * The component is intentionally light — we don't need fuzzy search,
 * keyboard hints or async option loading. A native <select> stays
 * consistent with the rest of the form, especially on iPad/Safari where
 * it gets the OS-native picker (one tap, big targets).
 */
function EditableSelect({ value, options, onChange, placeholder, addLabel, promptText, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  // Show selected value at top even if it's not in `options` (e.g. legacy
  // free-text values from before this component existed).
  const merged = useMemo(() => {
    if (!value) return options;
    return options.includes(value) ? options : [value, ...options];
  }, [options, value]);

  if (editing) {
    return (
      <div className="flex gap-1.5" data-testid={`${testid}-editor`}>
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={promptText}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const v = draft.trim();
              if (v) { onChange(v); setEditing(false); setDraft(""); }
            } else if (e.key === "Escape") {
              setEditing(false); setDraft("");
            }
          }}
          data-testid={`${testid}-input`}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            const v = draft.trim();
            if (v) { onChange(v); setEditing(false); setDraft(""); }
          }}
          data-testid={`${testid}-save`}
        >
          Save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => { setEditing(false); setDraft(""); }}
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <select
      className="h-9 w-full rounded-md border border-input px-3 text-sm bg-white"
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "__add__") {
          setDraft("");
          setEditing(true);
        } else {
          onChange(v);
        }
      }}
      data-testid={testid}
    >
      <option value="">{placeholder}</option>
      {merged.map((c) => <option key={c} value={c}>{c}</option>)}
      <option value="__add__">{addLabel}</option>
    </select>
  );
}


/* ─── Supplier select ──────────────────────────────────────────────────
 * Picks a supplier from the yacht's address book or falls back to a
 * free-form text input ("Other"). We keep the human-readable name in
 * `supplier` (so the existing inventory list rendering keeps working
 * without a join) AND a stable foreign key in `supplier_id` (for future
 * "click to view supplier card" deep-linking).
 */
function SupplierSelect({ suppliers, value, onChange }) {
  const { supplier_id, supplier } = value;
  // "Other" mode = free-form text without a supplier_id link. We default to
  // it whenever there's a typed name that doesn't match any known supplier
  // — that covers legacy items as well as fresh "Other" entries.
  const known = suppliers.find((s) => s.id === supplier_id);
  const isOther = !known && !!supplier;
  const [mode, setMode] = useState(isOther ? "other" : "select");

  // Keep the dropdown in sync if the parent swaps between editing items.
  useEffect(() => {
    setMode(isOther ? "other" : "select");
    /* eslint-disable-next-line */
  }, [supplier_id, supplier]);

  return (
    <div className="space-y-1.5" data-testid="inv-supplier-wrap">
      <select
        className="h-9 w-full rounded-md border border-input px-3 text-sm bg-white"
        value={mode === "other" ? "__other__" : (supplier_id || "")}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__other__") {
            setMode("other");
            onChange({ supplier_id: "", supplier: supplier || "" });
            return;
          }
          if (!v) {
            setMode("select");
            onChange({ supplier_id: "", supplier: "" });
            return;
          }
          const sup = suppliers.find((s) => s.id === v);
          setMode("select");
          onChange({ supplier_id: v, supplier: sup ? (sup.name || "") : "" });
        }}
        data-testid="inv-supplier"
      >
        <option value="">— No supplier —</option>
        {suppliers.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}{s.contact_person ? ` · ${s.contact_person}` : ""}
          </option>
        ))}
        <option value="__other__">Other (type below)</option>
      </select>
      {mode === "other" && (
        <Input
          value={supplier || ""}
          onChange={(e) => onChange({ supplier_id: "", supplier: e.target.value })}
          placeholder="Supplier name"
          data-testid="inv-supplier-other"
        />
      )}
    </div>
  );
}


/* ─── Reorder list dialog ──────────────────────────────────────────────
 * Builds a per-category "reorder list" of the low-stock items (quantity at
 * or below min) PLUS items expiring within 7 days (incl. expired), so the
 * crew can order replacements in the same pass. Items are grouped by
 * supplier; each group can be copied or emailed (mailto, prefilled with
 * that supplier's address when known). A suggested order quantity tops each
 * item back up to 2× its min stock; for expiring items it also covers
 * replacing the spoiling stock.
 */
function ReorderDialog({ group, items, suppliers, yachtName, onClose }) {
  const groups = useMemo(() => {
    if (!group) return [];
    const needed = items.filter(
      (i) => mainCategoryForItem(i) === group &&
        ((i.quantity ?? 0) <= (i.min_stock ?? 0) || (expiryDays(i) ?? 99) <= 7)
    );
    // Bucket by supplier (linked id → name+email; else the typed name; else none).
    const map = new Map();
    for (const it of needed) {
      const sup = it.supplier_id ? suppliers.find((s) => s.id === it.supplier_id) : null;
      const key = sup?.id || it.supplier || "__none__";
      if (!map.has(key)) {
        map.set(key, { name: sup?.name || it.supplier || "", email: sup?.email || "", items: [] });
      }
      map.get(key).items.push(it);
    }
    return [...map.values()].map((g) => ({
      ...g,
      items: g.items.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    }));
  }, [group, items, suppliers]);

  if (!group) return null;

  const isExpiring = (it) => (expiryDays(it) ?? 99) <= 7;
  const expiryNote = (it) => {
    const d = expiryDays(it);
    if (d === null || d > 7) return "";
    return d < 0 ? `expired ${it.expiry_date}` : `expires ${it.expiry_date}`;
  };
  const suggested = (it) => {
    const lowNeed = (it.min_stock ?? 0) * 2 - (it.quantity ?? 0);
    const expNeed = isExpiring(it) ? Math.max(it.quantity ?? 0, (it.min_stock ?? 0) * 2) : 0;
    return Math.max(lowNeed, expNeed, 1);
  };
  const itemLine = (it) =>
    `• ${it.name}${it.part_number ? ` [${it.part_number}]` : ""} — order ${suggested(it)} (have ${it.quantity ?? 0}, min ${it.min_stock ?? 0}${expiryNote(it) ? `, ${expiryNote(it)}` : ""})`;

  const groupText = (g) => {
    const header = `Reorder request — ${yachtName || "yacht"} — ${group}`;
    return [header, g.name ? `Supplier: ${g.name}` : "", "", ...g.items.map(itemLine)]
      .filter((l) => l !== null)
      .join("\n")
      .trim();
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Reorder list copied");
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };
  const emailGroup = (g) => {
    const subject = `Reorder request — ${yachtName || "yacht"} — ${group}`;
    window.location.href = `mailto:${g.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(groupText(g))}`;
  };

  const copyAll = () => copy(groups.map(groupText).join("\n\n"));
  const totalLow = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <Dialog open={!!group} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="reorder-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <ClipboardList className="h-5 w-5" /> Reorder list — {group}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {totalLow} low-stock or expiring item{totalLow !== 1 ? "s" : ""}, grouped by supplier. Suggested quantities top each item back up to 2× its minimum and cover replacing expiring stock.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3" data-testid="reorder-groups">
          {groups.map((g, idx) => (
            <div key={g.name || `none-${idx}`} className="rounded-md border border-zinc-200 p-3" data-testid={`reorder-group-${idx}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{g.name || "No supplier"}</div>
                  {g.email && <div className="text-[11px] text-zinc-500 truncate">{g.email}</div>}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => copy(groupText(g))} data-testid={`reorder-copy-${idx}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  {g.email && (
                    <Button size="sm" className="h-7 px-2 bg-ink hover:bg-black" onClick={() => emailGroup(g)} data-testid={`reorder-email-${idx}`}>
                      <Mail className="h-3.5 w-3.5 mr-1" /> Email
                    </Button>
                  )}
                </div>
              </div>
              <ul className="mt-2 space-y-1">
                {g.items.map((it) => (
                  <li key={it.id} className="text-[12px] text-zinc-700 flex items-baseline justify-between gap-2">
                    <span className="min-w-0 truncate">
                      {it.name}{it.part_number ? <span className="text-zinc-400 font-mono"> [{it.part_number}]</span> : null}
                      {expiryNote(it) && (
                        <span className={`ml-1.5 ${(expiryDays(it) ?? 0) < 0 ? "text-red-600" : "text-amber-700"}`} data-testid={`reorder-expiry-${it.id}`}>
                          {expiryNote(it)}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-zinc-500">
                      order <span className="font-mono font-medium text-amber-700">{suggested(it)}</span>
                      <span className="text-zinc-400"> · have {it.quantity ?? 0}/min {it.min_stock ?? 0}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} data-testid="reorder-close">Close</Button>
          <Button onClick={copyAll} className="bg-ink hover:bg-black" data-testid="reorder-copy-all">
            <Copy className="h-4 w-4 mr-2" /> Copy all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative px-4 py-2 text-sm transition-colors ${
        active ? "text-ink" : "text-zinc-500 hover:text-zinc-700"
      }`}
    >
      {children}
      {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-ink" />}
    </button>
  );
}
export function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 text-left font-medium ${className}`}>{children}</th>;
}
export function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
}
export function EmptyYacht() {
  return (
    <div className="p-10 max-w-2xl">
      <div className="rounded-md border border-zinc-200 bg-white p-8" data-testid="empty-yacht">
        <div className="font-display text-xl">Select or create a yacht</div>
        <div className="text-sm text-zinc-600 mt-2">Use the switcher in the sidebar, or go to Yachts to add one.</div>
      </div>
    </div>
  );
}
